import { create } from 'zustand'
import type { Session } from '@/types'
import { 
  mockSessions, 
  getUpcomingSessions, 
  getTodaysSessions, 
  getSessionsByDateRange,
  getCompletedSessions 
} from '@/data'

interface SessionState {
  // Data
  sessions: Session[]
  selectedSession: Session | null
  
  // UI State
  isAddSessionModalOpen: boolean
  isEditSessionModalOpen: boolean
  isSessionDetailModalOpen: boolean
  selectedDate: Date
  viewMode: 'month' | 'week' | 'day' | 'list'
  
  // Actions
  setSessions: (sessions: Session[]) => void
  addSession: (session: Omit<Session, 'id' | 'createdAt' | 'updatedAt'>) => void
  updateSession: (sessionId: string, updates: Partial<Session>) => void
  deleteSession: (sessionId: string) => void
  setSelectedSession: (session: Session | null) => void
  
  // Modal actions
  openAddSessionModal: () => void
  closeAddSessionModal: () => void
  openEditSessionModal: (session: Session) => void
  closeEditSessionModal: () => void
  openSessionDetailModal: (session: Session) => void
  closeSessionDetailModal: () => void
  
  // View actions
  setSelectedDate: (date: Date) => void
  setViewMode: (mode: 'month' | 'week' | 'day' | 'list') => void
  
  // Data fetchers
  getUpcomingSessions: (coachId: string, days?: number) => Session[]
  getTodaysSessions: (coachId: string) => Session[]
  getSessionsByDateRange: (coachId: string, startDate: Date, endDate: Date) => Session[]
  getCompletedSessions: (coachId: string, limit?: number) => Session[]
  getSessionsByDate: (date: Date) => Session[]
  getSessionsByWeek: (date: Date) => Session[]
}

export const useSessionStore = create<SessionState>((set, get) => ({
  // Initial data
  sessions: mockSessions,
  selectedSession: null,
  
  // Initial UI state
  isAddSessionModalOpen: false,
  isEditSessionModalOpen: false,
  isSessionDetailModalOpen: false,
  selectedDate: new Date(),
  viewMode: 'month',
  
  // Actions
  setSessions: (sessions) => set({ sessions }),
  
  addSession: (sessionData) => {
    const newSession: Session = {
      ...sessionData,
      id: `session-${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date()
    }
    
    set((state) => ({
      sessions: [...state.sessions, newSession],
      isAddSessionModalOpen: false
    }))
  },
  
  updateSession: (sessionId, updates) => {
    set((state) => ({
      sessions: state.sessions.map(session =>
        session.id === sessionId
          ? { ...session, ...updates, updatedAt: new Date() }
          : session
      ),
      selectedSession: state.selectedSession?.id === sessionId
        ? { ...state.selectedSession, ...updates, updatedAt: new Date() }
        : state.selectedSession,
      isEditSessionModalOpen: false
    }))
  },
  
  deleteSession: (sessionId) => {
    set((state) => ({
      sessions: state.sessions.filter(session => session.id !== sessionId),
      selectedSession: state.selectedSession?.id === sessionId ? null : state.selectedSession,
      isEditSessionModalOpen: false,
      isSessionDetailModalOpen: false
    }))
  },
  
  setSelectedSession: (session) => set({ selectedSession: session }),
  
  // Modal actions
  openAddSessionModal: () => set({ isAddSessionModalOpen: true }),
  closeAddSessionModal: () => set({ isAddSessionModalOpen: false }),
  
  openEditSessionModal: (session) => set({ 
    selectedSession: session, 
    isEditSessionModalOpen: true 
  }),
  closeEditSessionModal: () => set({ isEditSessionModalOpen: false }),
  
  openSessionDetailModal: (session) => set({ 
    selectedSession: session, 
    isSessionDetailModalOpen: true 
  }),
  closeSessionDetailModal: () => set({ isSessionDetailModalOpen: false }),
  
  // View actions
  setSelectedDate: (date) => set({ selectedDate: date }),
  setViewMode: (mode) => set({ viewMode: mode }),
  
  // Data fetchers
  getUpcomingSessions: (coachId, days) => {
    return getUpcomingSessions(coachId, days)
  },
  
  getTodaysSessions: (coachId) => {
    return getTodaysSessions(coachId)
  },
  
  getSessionsByDateRange: (coachId, startDate, endDate) => {
    return getSessionsByDateRange(coachId, startDate, endDate)
  },
  
  getCompletedSessions: (coachId, limit) => {
    return getCompletedSessions(coachId, limit)
  },
  
  getSessionsByDate: (date) => {
    const { sessions } = get()
    const targetDate = new Date(date)
    targetDate.setHours(0, 0, 0, 0)
    
    return sessions.filter(session => {
      const sessionDate = new Date(session.date)
      sessionDate.setHours(0, 0, 0, 0)
      return sessionDate.getTime() === targetDate.getTime()
    }).sort((a, b) => a.startTime.localeCompare(b.startTime))
  },
  
  getSessionsByWeek: (date) => {
    const { sessions } = get()
    const startOfWeek = new Date(date)
    const day = startOfWeek.getDay()
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1) // Adjust for Monday start
    startOfWeek.setDate(diff)
    startOfWeek.setHours(0, 0, 0, 0)
    
    const endOfWeek = new Date(startOfWeek)
    endOfWeek.setDate(startOfWeek.getDate() + 6)
    endOfWeek.setHours(23, 59, 59, 999)
    
    return sessions.filter(session => {
      const sessionDate = new Date(session.date)
      return sessionDate >= startOfWeek && sessionDate <= endOfWeek
    }).sort((a, b) => {
      const dateCompare = a.date.getTime() - b.date.getTime()
      if (dateCompare === 0) {
        return a.startTime.localeCompare(b.startTime)
      }
      return dateCompare
    })
  }
}))